import { capitalize } from 'lodash-es'

import { SESSION_DURATION_OPTIONS, ZOOM_MAX_PARTICIPANTS } from '@/utils/constants'
import getTimeOptions from '@/utils/getTimeOptions'
import { MINUTES_PER_HOUR } from '@/utils/time'

import { SESSION_ATTENDANCE_POLICIES } from '@/queries/constants'
import type { SeriesTemplateDetails, SessionTemplateDetails } from '@/client/services/template'

import type { FormInputType } from '@/components/UI/Form/types'

import { getTutorQuestionnaireQuestion, type TutorQuestionnaire } from '../tutorQuestionnaire'

export default function getSessionTemplateFormInputs({
  sessionTemplate,
  seriesTemplate,
  topicOptions,
  worldOptions,
}: {
  sessionTemplate?: Omit<SessionTemplateDetails, 'id'> | null
  seriesTemplate?: SeriesTemplateDetails | null
  topicOptions: { label: string; options: { label: string; value: string }[] }[]
  worldOptions: { label: string; value: string }[]
}): FormInputType[] {
  const initialWorldId = sessionTemplate?.worldId ?? seriesTemplate?.worldId

  const initialMaxLearners = sessionTemplate?.maxLearners ?? seriesTemplate?.maxLearners

  const initialAllowCohosts = sessionTemplate?.allowCohosts ?? seriesTemplate?.allowCohosts

  const initialAttendancePolicy = sessionTemplate?.attendancePolicy ?? 'required'

  const initialActive = sessionTemplate?.active ?? seriesTemplate?.active

  const date = sessionTemplate?.startDate?.toISO()
  const time =
    sessionTemplate?.startDate == null
      ? undefined
      : `${sessionTemplate.startDate.hour + sessionTemplate.startDate.minute / MINUTES_PER_HOUR}`

  const initialBreakoutEnabled = sessionTemplate?.isBreakoutEnabled ?? false

  const formInputs: FormInputType[] = [
    {
      type: 'select-group',
      name: 'topicId',
      label: 'Topic',
      initialValue: sessionTemplate?.topic?.id == null ? null : `${sessionTemplate.topic.id}`,
      options: topicOptions,
      size: 6,
      required: false,
    },
    {
      type: 'number',
      name: 'seriesPosition',
      label: 'Series Position',
      initialValue: sessionTemplate?.seriesPosition == null ? null : sessionTemplate.seriesPosition,
      disabled: seriesTemplate == null,
      size: 6,
      required: seriesTemplate != null,
    },
    {
      label: 'Active?',
      sublabel: 'Can tutor access this right now?',
      type: 'radio',
      name: 'active',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
      initialValue: initialActive == null ? null : initialActive ? 'yes' : 'no',
      disabled: seriesTemplate != null,
      required: true,
      size: 6,
    },
    {
      label: 'Subworld',
      type: 'select',
      name: 'worldId',
      options: worldOptions,
      initialValue: initialWorldId == null ? '' : `${initialWorldId}`,
      disabled: seriesTemplate != null,
      size: 6,
    },
    {
      label: 'Title',
      type: 'text',
      name: 'title',
      initialValue: sessionTemplate?.title,
      required: true,
      size: 6,
    },
    {
      label: 'External Description',
      sublabel: 'This is the label that tutor can see. Not used for series template sessions.',
      type: 'rich-text',
      name: 'externalDescription',
      initialValue: seriesTemplate != null ? null : sessionTemplate?.externalDescription,
      required: seriesTemplate == null,
      disabled: seriesTemplate != null,
      size: 6,
    },
    {
      label: 'Description',
      sublabel: 'This is for learners to see.',
      type: 'rich-text',
      name: 'description',
      initialValue: sessionTemplate?.description,
      required: true,
      size: 6,
    },
    {
      label: 'Welcome Question',
      type: 'textarea',
      name: 'welcomeQuestion',
      initialValue: getTutorQuestionnaireQuestion(
        (sessionTemplate?.questionnaire as TutorQuestionnaire) ?? null
      ),
      disabled: seriesTemplate != null,
      required: false,
      size: 6,
    },
    {
      type: 'date',
      label: 'Date',
      name: 'date',
      size: 6,
      initialValue: date ?? undefined,
      required: false,
    },
    {
      type: 'select',
      label: 'Time',
      name: 'time',
      size: 6,
      initialValue: time,
      required: false,
      options: getTimeOptions(),
    },
    {
      label: 'Duration',
      type: 'select',
      name: 'durationMinutes',
      options: SESSION_DURATION_OPTIONS.map(item => ({
        value: `${item}`,
        label: `${item}`,
      })),
      required: true,
      initialValue:
        sessionTemplate?.durationMinutes == null ? null : `${sessionTemplate?.durationMinutes}`,
      size: 6,
    },
    {
      label: 'Max Learners',
      type: 'number',
      name: 'maxLearners',
      sublabel: `You should not submit a number greater than ${ZOOM_MAX_PARTICIPANTS}`,
      required: true,
      initialValue: initialMaxLearners ?? null,
      disabled: seriesTemplate != null,
      size: 6,
      max: ZOOM_MAX_PARTICIPANTS,
      min: 1,
    },
    {
      label: 'Allow Co-hosting',
      type: 'radio',
      name: 'allowCohosts',
      required: true,
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
      initialValue: initialAllowCohosts == null ? null : initialAllowCohosts ? 'yes' : 'no',
      disabled: seriesTemplate != null,
      size: 6,
    },
    {
      label: 'Attendance Policy',
      type: 'select',
      name: 'attendancePolicy',
      options: SESSION_ATTENDANCE_POLICIES.map(item => ({
        value: item,
        label: capitalize(item),
      })),
      initialValue: initialAttendancePolicy,
      size: 6,
      required: true,
    },
    {
      label: 'Curriculum',
      sublabel:
        'This is for tutors to see. Why should they host it? This is not used for series template sessions.',
      type: 'rich-text',
      editorType: 'advanced',
      name: 'curriculum',
      initialValue: seriesTemplate != null ? null : sessionTemplate?.curriculum,
      required: seriesTemplate == null,
      disabled: seriesTemplate != null,
      size: 6,
    },
    {
      label: 'Enable Breakout Sessions',
      type: 'radio',
      name: 'isBreakoutEnabled',
      sublabel: 'Are breakout sessions enabled?',
      required: true,
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
      initialValue: initialBreakoutEnabled ? 'yes' : 'no',
      size: 6,
    },
  ]

  return formInputs
}
